import { PaymentService, PaymentStatusResponseDto, InvoicePayment, TransactionType, TerminalMode } from "@services";
import { makeAutoObservable, runInAction } from 'mobx';
import { L } from '@lib';
import { Utils } from '@utils';

class PaymentStore {
  loading: boolean = false;
  isTerminalEnabled: boolean = false;

  // Payment processing properties
  processingPayment: boolean = false;
  processingStatus: PaymentStatusResponseDto | null = null;
  paymentFinished: boolean = false;
  trackingId: string | null = null;
  cardPaymentLoading: boolean = false;
  cashPaymentLoading: boolean = false;
  isProcessingPaymentRef: boolean = false;

  // Private properties for intervals
  private pollingInterval: NodeJS.Timeout | null = null;
  private closeTimeout: NodeJS.Timeout | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  setLoading = (value: boolean) => {
    runInAction(() => {
      this.loading = value;
    });
  };

  setTerminalEnabled = (value: boolean) => {
    runInAction(() => {
      this.isTerminalEnabled = value;
    });
  };

  checkTerminalStatus = async () => {
    this.setLoading(true);
    try {
      const terminalEnabled = await PaymentService.hasTerminalEnabled();
      this.setTerminalEnabled(terminalEnabled);
      return terminalEnabled;
    } catch (error) {
      console.error('Failed to check terminal status:', error);
      this.setTerminalEnabled(false);
      return false;
    } finally {
      this.setLoading(false);
    }
  };

  clearTerminalStatus = () => {
    this.resetPaymentState();
    runInAction(() => {
      this.isTerminalEnabled = false;
      this.loading = false;
    });
  };

  setProcessingPayment = (value: boolean) => {
    runInAction(() => {
      this.processingPayment = value;
    });
  };

  setProcessingStatus = (status: PaymentStatusResponseDto | null) => {
    runInAction(() => {
      this.processingStatus = status;
    });
  };

  setPaymentFinished = (value: boolean) => {
    runInAction(() => {
      this.paymentFinished = value;
    });
  };

  setTrackingId = (id: string | null) => {
    runInAction(() => {
      this.trackingId = id;
    });
  };

  setCardPaymentLoading = (value: boolean) => {
    runInAction(() => {
      this.cardPaymentLoading = value;
    });
  };

  setCashPaymentLoading = (value: boolean) => {
    runInAction(() => {
      this.cashPaymentLoading = value;
    });
  };

  setIsProcessingPaymentRef = (value: boolean) => {
    runInAction(() => {
      this.isProcessingPaymentRef = value;
    });
  };

  get hasPaymentError() {
    return this.processingStatus?.status === 'FAILED' || this.processingStatus?.status === 'ERROR' || this.processingStatus?.status === 'TIMEOUT';
  }

  get signatureRequired(){
    return this.processingStatus?.status === 'SignatureVerificationRequired';
  }

  // Clear all intervals and timeouts
  clearIntervals = () => {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  };

  resetPaymentState = () => {
    runInAction(() => {
      this.processingPayment = false;
      this.processingStatus = null;
      this.paymentFinished = false;
      this.trackingId = null;

      this.cardPaymentLoading = false;
      this.cashPaymentLoading = false;
      this.isProcessingPaymentRef = false;
    });
    this.clearIntervals();
  };

  verifySignature = async (trackingId: string, isValid: boolean) => {
  try {
    console.log(`Verifying signature for tracking ID: ${trackingId}, valid: ${isValid}`);
    const result = await PaymentService.verifySignature({ trackingId, isValid });
    return result;
  } catch (error) {
    setTimeout(() => this.resetPaymentState(), 3000);
  }
};
  processCardPayment = async (
    invoiceId: string, 
    amount: number, 
    terminalMode: TerminalMode,
    onPaymentSuccess: (paymentData: InvoicePayment) => Promise<void>
  ) => {
    this.resetPaymentState();
    this.setCardPaymentLoading(true);
    this.setProcessingStatus(null);
    
    try {
      const roundedAmount = Utils.roundToTwoDecimals(amount);

      //investigate why it fails with InvoicePayment type
      const originalPaymentValues: any = {
          amount: roundedAmount,
          method: TerminalMode.Sale ? 'Card' : 'CardRefund',
          //note: "",
          transactionType: TransactionType.Payment,
          //id: "",
          //invoiceCreditNoteId: "",
          invoiceId: invoiceId,
          //creationTime: new Date(),
         // createdBy: ""
      };

      this.setIsProcessingPaymentRef(false);
      this.setProcessingPayment(true);
      this.setProcessingStatus({
        status: 'INITIALIZING',
        statusMessage: L('Initializing payment...'),
        order: 1,
      });
      this.setPaymentFinished(false);

      // Create tracking session
      const trackingResponse = await PaymentService.createPaymentTrackingSession({
        invoiceId,
        amount: roundedAmount
      });

      this.setTrackingId(trackingResponse.id);

      // Start payment processing (don't await)
      PaymentService.processPayment({
        invoiceId,
        amount: roundedAmount,
        paymentTrackingId: trackingResponse.id,
        terminalMode: terminalMode
      });

      // Start polling for status
      this.startStatusPolling(trackingResponse.id, originalPaymentValues, onPaymentSuccess);

    } catch (error) {
      console.error("Payment initialization error:", error);
      this.setProcessingStatus({
        status: 'ERROR',
        statusMessage: L('Payment initialization failed. Please try again.'),
        order: this.processingStatus?.order || 1
      });
      this.setIsProcessingPaymentRef(false);
      this.setCardPaymentLoading(false);
      setTimeout(() => this.setProcessingPayment(false), 3000);
      setTimeout(() => this.resetPaymentState(), 3000);
    }
  };

  // Status polling method
  private startStatusPolling = (
    trackingId: string, 
    originalPaymentValues: InvoicePayment, 
    onPaymentSuccess: (paymentData: InvoicePayment) => Promise<void>
  ) => {
    this.pollingInterval = setInterval(async () => {
      try {
        const statusResult = await PaymentService.getPaymentStatus(trackingId);
        this.setProcessingStatus(statusResult);

        if (statusResult.status === "PAID" && !this.isProcessingPaymentRef) {
          this.setIsProcessingPaymentRef(true);
          this.setPaymentFinished(true);

          if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
          }

          try {
            this.setCardPaymentLoading(false);
            //check to pass tracking, if its the current one
            await onPaymentSuccess(originalPaymentValues).then(() => {
              // Pass the tracking ID to the success handler
              if (trackingId) {
                console.log('Payment successful with tracking ID:', trackingId);
                console.log('Payment data:', originalPaymentValues);
              }
            });
          } catch (error) {
            console.error('Error processing payment success:', error);
          }

          this.closeTimeout = setTimeout(() => {
            this.setProcessingPayment(false);
            this.setPaymentFinished(false);
            this.setIsProcessingPaymentRef(false);
            this.setTrackingId(null);
          }, 5000);

        } else if (["FAILED", "ERROR", "TIMEOUT"].includes(statusResult.status)) {
          this.setIsProcessingPaymentRef(false);
          this.setCardPaymentLoading(false);
          this.setProcessingStatus({
            status: statusResult.status,
            statusMessage: statusResult.statusMessage || L("Payment failed"),
            order: statusResult.order
          });
          this.setPaymentFinished(true);

          if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
          }
        }
      } catch (error) {
        console.error("Error polling payment status:", error);
        this.setCardPaymentLoading(false);
      }
    }, 500);
  };

  // Cleanup method
  cleanup = () => {
    this.clearIntervals();
    this.resetPaymentState();
  };
}

export default PaymentStore;